import { useState } from 'react';

import { PaneState } from '@/sections/insights/hunts/PaneState';
import { useHuntConversations } from '@/sections/insights/hunts/useHunts';
import type { Conversation, Hunt } from '@/types';

import { ConversationNode } from './ConversationNode';
import { ConversationView } from './ConversationView';
import { sortByCreatedDesc } from './sortByCreatedDesc';

interface ChatPaneProps {
  hunt: Hunt;
  initialConvId?: string;
}

export function ChatPane({ hunt, initialConvId }: ChatPaneProps) {
  const { data: conversations = [], isLoading } = useHuntConversations(
    hunt.uuid
  );

  // Newest iterations on top. Cheap derivation — React Compiler handles
  // memoization, no manual useMemo needed.
  const sortedConversations = sortByCreatedDesc(conversations);

  // The user's explicit pick wins; otherwise default to the running
  // iteration if any, else the most recent one. Subagent conversations
  // live outside this root list (they're fetched per-parent by
  // ConversationNode), so we trust the pick verbatim rather than gating
  // on root-list membership.
  const [userPick, setUserPick] = useState<string | null>(
    initialConvId ?? null
  );
  const selectedConv = pickSelectedConv(
    userPick,
    sortedConversations,
    hunt.currentConvId
  );

  return (
    <PaneState
      isLoading={isLoading}
      isEmpty={sortedConversations.length === 0}
      empty={{
        title: 'No conversations yet',
        description:
          'A conversation will appear once the cron sweep enqueues an iteration.',
      }}
    >
      <div className="flex h-full">
        <div className="bg-chariot-background-secondary/40 w-56 shrink-0 overflow-y-auto">
          <ul>
            {sortedConversations.map(c => (
              <ConversationNode
                key={c.uuid}
                conversation={c}
                depth={0}
                selected={selectedConv}
                onSelect={setUserPick}
                runningId={hunt.currentConvId}
              />
            ))}
          </ul>
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          {selectedConv ? (
            <ConversationView
              convId={selectedConv}
              isRunning={selectedConv === hunt.currentConvId}
            />
          ) : (
            <div className="text-chariot-text-secondary p-4 text-sm">
              Select a conversation to view.
            </div>
          )}
        </div>
      </div>
    </PaneState>
  );
}

function pickSelectedConv(
  userPick: string | null,
  sorted: readonly Conversation[],
  runningId: string | undefined
): string | null {
  if (userPick) return userPick;
  if (sorted.length === 0) return null;
  const running = runningId
    ? sorted.find(c => c.uuid === runningId)
    : undefined;
  return (running ?? sorted[0]).uuid;
}
